<script setup lang="ts">
import { IconArrowRight } from '@tabler/icons-vue'
import { computed } from 'vue'

import { stripHtml } from '@tix/common-utils'
import BaseBadge from '@tix/ds/components/atoms/BaseBadge.vue'
import BaseIcon from '@tix/ds/components/atoms/BaseIcon.vue'
import RouterLinkActionCard from '@tix/ds/components/molecules/RouterLinkActionCard.vue'
import { FlowDefinition } from '@tix/entity'

import { formatGalleryImages } from '@/helpers/imageHelpers'

// TODO add image thumbnail component to the design system
import '@tix/ds/styles/portico/component/button.css'
import '@tix/ds/styles/portico/component/image-thumbnail.css'

export type Props = {
  flow: FlowDefinition
}

const props = defineProps<Props>()

const image = computed(() => formatGalleryImages(props.flow.data.meta.images)[0] ?? null)
const linkLabel = computed(() => stripHtml(props.flow.data.name))
const route = computed(() => `/flows/${props.flow.id}`)
</script>

<template>
  <RouterLinkActionCard :aria-label="linkLabel" class="bundle-listing-item-card" padding="none" :to="route">
    <article class="bundle-listing-item">
      <figure v-if="image" class="thumbnail" :class="{ 'no-crop': image.noCrop }">
        <img :alt="image.alt" :src="image.src" />
      </figure>

      <div class="copy">
        <h2 v-shtml="flow.data.name" class="title" />
        <BaseBadge
          v-if="flow.data.meta.sold_out"
          corners="pill"
          label="SOLD OUT"
          size="xs"
          type="danger"
          variant="filled"
        />
        <div v-if="flow.data.meta.summary" v-shtml="flow.data.meta.summary" class="summary" />

        <span aria-hidden="true" class="cta">
          Build
          <BaseIcon :icon="IconArrowRight" :stroke="1.5" />
        </span>
      </div>
    </article>
  </RouterLinkActionCard>
</template>

<style scoped>
.bundle-listing-item-card,
.bundle-listing-item {
  height: 100%;
}

.bundle-listing-item-card :deep(.content) {
  height: 100%;
}

.bundle-listing-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
}

.thumbnail {
  position: relative;
  display: block;
  flex-shrink: 0;
  width: 100%;
  height: 203px;
  margin: 0;
  overflow: hidden;
  background: var(--image-thumbnail-image-bg);
  border-bottom: var(--border-default) solid var(--image-thumbnail-image-border);

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  &.no-crop img {
    object-fit: contain;
  }
}

.copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--padding-compact);
  padding-block: var(--padding-compact);
  padding-inline: var(--padding-comfy);
}

.title {
  margin: 0;
}

.summary {
  font-size: var(--font-size-body-large);
  line-height: 1.5;

  :deep(p) {
    margin: 0;
  }
}

.cta {
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  gap: var(--space-0-25);
  margin-top: auto;
  color: var(--button-tertiary-confirm-text);
  font-family: var(--font-family-body);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-regular);
  line-height: 1.5;
}
</style>
